'use client';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ControlledInput } from '@/components/form/ControlledInput/ControlledInput';
import { ControlledDropdown } from '@/components/form/ControlledDropdown/ControlledDropdown';
import { ControlledInputWithMask } from '@/components/form/ControlledInputWithMask/ControlledInputWithMask';
import {
  ControlledScoreSelect,
} from '@/components/features/CreateReviewModal/ControlledScoreSelect/ControlledScoreSelect';
import { ControlledCheckbox } from '@/components/form/ControlledCheckbox/ControlledCheckbox';
import { ControlledTextArea } from '@/components/form/ControlledTextArea/ControlledTextArea';
import { SuccessPopup } from '@/components/common/SuccessPopup/SuccessPopup';
import { ActionButton } from '@/components/ui/ActionButton/ActionButton';
import { Tab, Tabs } from '@/components/features/Tabs/Tabs';
import { createTestimonial } from '@/api/testimonials/testimonials';
import { getDoctorsList } from '@/api/doctors/doctors';
import { getFullName } from '@/utils/strings';
import { CreateReviewForm, createReviewFormSchema } from '@/components/features/CreateReviewModal/CreateReviewSchema';
import {
  AboutKeys,
  aboutTabs,
  ContactKeys,
  contactsTabs,
} from '@/components/features/CreateReviewModal/CreateReviewModal.utils';
import s from './CreateReviewModal.module.scss';
import { TextLink } from '@/components/ui/TextLink/TextLink';
import { API_URL } from '@/utils/constants';

const personal = `${API_URL}/consents/get-current?document_type=%D0%94%D0%BE%D0%BA%D1%83%D0%BC%D0%B5%D0%BD%D1%82%20%D1%81%D0%BE%D0%B3%D0%BB%D0%B0%D1%81%D0%B8%D1%8F`;

export const CreateReviewModal = ({ onSuccess }: { onSuccess: () => void }) => {
  const [successModal, setSuccessModal] = useState(false);
  const [doctors, setDoctors] = useState<{ id: number, name: string }[]>([]);
  const createForm = useForm<CreateReviewForm>({
    resolver: zodResolver(createReviewFormSchema),
    defaultValues: {
      contact: 'phone',
      about: 'clinic',
      author_name: '',
      phone: '',
      email: '',
      text: '',
      score: 5,
      publication_consent: false,
    },
  });

  useEffect(() => {
    fetchDoctors();
  }, []);

  const [contact, about] = createForm.watch(['contact', 'about']);

  const fetchDoctors = async () => {
    const doctors = await getDoctorsList({
      page: 1,
      search: '',
      per_page: 100,
    });
    const transformedDoctors = doctors.items.map((doctor) => {
      return { id: doctor.id, name: getFullName(doctor, true) };
    });
    setDoctors(transformedDoctors);
  };

  const submit = async (form: CreateReviewForm) => {
    const { about, contact, ...data } = form;

    try {
      await createTestimonial({
        ...data,
        doctor_id: data.doctor_id ? data.doctor_id.id : null,
        phone: contact === 'phone' ? data.phone : null,
        email: contact === 'email' ? data.email : null,
      });
      setSuccessModal(true);
      createForm.reset();
    } catch (e) {
      console.error(e);
    }
  };

  const closeSuccessModal = () => {
    setSuccessModal(false);
    onSuccess();
  };

  const onChangeContact = (tab: Tab<ContactKeys>) => createForm.setValue('contact', tab.key);

  const onChangeAbout = (tab: Tab<AboutKeys>) => createForm.setValue('about', tab.key);

  if (successModal) return (
    <SuccessPopup
      onClose={closeSuccessModal}
      title={'Ваш отзыв успешно отправлен'}
      text={'Спасибо'}
    />
  );

  return (
    <FormProvider {...createForm} >
      <div className={s.form}>
        <h3 className={s.form__title}>Оставить отзыв</h3>
        <div className={s.form__wrapper}>
          <h4>Контактные данные</h4>
          <ControlledInput fieldName={'author_name'} placeholder={'Ваше имя'} label={'Имя'} />
          <Tabs
            tabs={contactsTabs}
            activeTabKey={contact}
            setActiveTab={onChangeContact}
          />
          {contact === 'phone' && (
            <ControlledInputWithMask
              fieldName={'phone'}
              maskOptions={[{ mask: '+{7}(000)000-00-00' }]}
              placeholder={'+7 (___) ___ -__-__'}
              label={'Номер телефона'}
            />
          )}
          {contact === 'email' && (
            <ControlledInput
              fieldName={'email'}
              label={'Email'}
            />
          )}
        </div>
        <div className={s.form__wrapper}>
          <h4>Отзыв</h4>
          <Tabs
            tabs={aboutTabs}
            activeTabKey={about}
            setActiveTab={onChangeAbout}
          />
          {about === 'doctor' && (
            <ControlledDropdown
              fieldName={'doctor_id'}
              placeholder={'Выберите врача'}
              options={doctors}
              className={s.form__dropdown}
            />
          )}
          <ControlledScoreSelect fieldName={'score'} label={'Оценка'} className={s.form__score} />
          <ControlledTextArea fieldName={'text'} label={'Сообщение'} />
        </div>
        <ControlledCheckbox fieldName={'publication_consent'}>
          <p>Я даю согласие на публикацию моего отзыва на сайте клиники</p>
        </ControlledCheckbox>
        {/*<div>captcha</div>*/}
        <ActionButton onClick={createForm.handleSubmit(submit)} className={s.form__button}>Отправить</ActionButton>
        <p className={s.form__caption}>
          Нажимая на кнопку Отправить, я даю <TextLink text={'согласие на обработку персональных данных'} href={personal} target={'_blank'} /> и
          подтверждаю достоверность
          указанной информации. <br />
          *Администрация сайта оставляет за собой право производить модерацию отзывов и принимать решение по
          размещению отзывов по своему усмотрению
        </p>
      </div>
    </FormProvider>
  );
};